// src/features/TeamChat/api/teamChat.cache.ts
import {
  TEAM_MEMBERS_TAG,
  TEAM_MESSAGES_TAG,
  TEAM_REQUESTS_TAG,
  TEAM_SUMMARY_TAG,
  patchMessages,
  patchRequest,
  teamChatApi,
} from './teamChat.api'
import type { TeamDispatch } from './teamChat.api'
import type { TeamMessage, TeamRequest } from './teamChat.types'

type GetState = () => unknown

export const SUMMARY_REFRESH_MS = 1000

const isPending = (entry: { status?: string } | undefined) => entry?.status === 'pending'

// A patch made while a refetch is in flight would be overwritten; RTK's delayed invalidation refetches after it settles.
export const applyTeamMessage =
  (message: TeamMessage) => (dispatch: TeamDispatch, getState: GetState) => {
    dispatch(patchMessages([message]))
    const entry = teamChatApi.endpoints.getTeamMessages.select()(getState() as never)
    if (isPending(entry)) dispatch(teamChatApi.util.invalidateTags([TEAM_MESSAGES_TAG]))
  }

export const applyTeamRequest =
  (request: TeamRequest) => (dispatch: TeamDispatch, getState: GetState) => {
    dispatch(patchRequest(request))
    const pending = (['active', 'done'] as const).some((filter) =>
      isPending(teamChatApi.endpoints.getTeamRequests.select(filter)(getState() as never)),
    )
    if (pending) dispatch(teamChatApi.util.invalidateTags([TEAM_REQUESTS_TAG]))
  }

let summaryTimer: ReturnType<typeof setTimeout> | undefined

const isPageHidden = () => typeof document !== 'undefined' && document.visibilityState !== 'visible'

// Many events in a burst cost one summary request; a hidden tab waits for the visibility resync.
export const scheduleSummaryRefresh = () => (dispatch: TeamDispatch) => {
  if (summaryTimer || isPageHidden()) return
  summaryTimer = setTimeout(() => {
    summaryTimer = undefined
    dispatch(teamChatApi.util.invalidateTags([TEAM_SUMMARY_TAG]))
  }, SUMMARY_REFRESH_MS)
}

// Tag invalidation refetches only what is subscribed: the roster and summary always, lists only on screen.
export const resyncTeam = () => (dispatch: TeamDispatch) => {
  dispatch(
    teamChatApi.util.invalidateTags([
      TEAM_MEMBERS_TAG,
      TEAM_SUMMARY_TAG,
      TEAM_MESSAGES_TAG,
      TEAM_REQUESTS_TAG,
    ]),
  )
}
